import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, LogOut, Package } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { getProductBySlug } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const [account, setAccount] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);

  const loadAccount = () =>
    axios.get(`${API}/customer/me`, { withCredentials: true })
      .then((r) => setAccount(r.data))
      .catch(() => setAccount(null));

  useEffect(() => {
    loadAccount().finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !orderId.trim()) { toast.error("Enter your email and Order ID."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/customer/login`, { email: email.trim(), order_id: orderId.trim() }, { withCredentials: true });
      await loadAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/customer/logout`, {}, { withCredentials: true });
    setAccount(null);
    toast.success("Logged out.");
  };

  if (checking) return <p className="text-center py-32 text-white/50">Loading…</p>;

  if (!account) {
    return (
      <section className="max-w-md mx-auto px-5 py-16 sm:py-24">
        <SEO title="My Orders" description="View your Dripchexk order history using your email and Order ID." />
        <p className="font-mono-cap text-[10px] text-[#FF69B4] tracking-[0.3em]">MY ORDERS</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2">Welcome back.</h1>
        <p className="text-white/50 text-sm mt-3">Log in with the email you used at checkout + any of your Order IDs (looks like TDC1005). No password needed.</p>
        <form onSubmit={login} className="mt-10 space-y-5">
          <div>
            <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Email</label>
            <input
              data-testid="account-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-[#0e0e0e] border border-white/15 px-4 py-3.5 text-sm focus:border-white outline-none"
            />
          </div>
          <div>
            <label className="font-mono-cap text-[10px] text-white/40 block mb-2">Order ID</label>
            <input
              data-testid="account-order-input"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="TDC1005"
              className="w-full bg-[#0e0e0e] border border-white/15 px-4 py-3.5 text-sm focus:border-white outline-none uppercase placeholder:normal-case"
            />
          </div>
          <button
            type="submit"
            data-testid="account-login-btn"
            disabled={loading}
            className="w-full bg-white text-black font-mono-cap text-xs py-4 hover:bg-[#FF69B4] hover:text-white transition-colors disabled:opacity-50"
          >
            {loading ? "Checking…" : "View My Orders"}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid="account-dashboard">
      <SEO title="My Orders" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-cap text-[10px] text-[#FF69B4] tracking-[0.3em]">MY ORDERS</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2" data-testid="account-name">
            {account.name ? account.name.split(" ")[0] : "You"}.
          </h1>
          <p className="text-white/40 text-xs mt-2">{account.email}</p>
        </div>
        <button data-testid="account-logout-btn" onClick={logout} className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs font-mono-cap hover:bg-white/5">
          <LogOut size={13} /> Log out
        </button>
      </div>

      <div className="mt-10 space-y-4">
        {account.orders.map((o) => (
          <div key={o.id} data-testid="account-order-card" className="border border-white/10 bg-[#0a0a0a] p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Package size={16} className="text-[#FF69B4]" />
                <p className="font-serif text-xl">{o.order_number}</p>
                <span className="font-mono-cap text-[9px] px-2 py-1 bg-white/10 uppercase">{o.status}</span>
              </div>
              <p className="text-[11px] text-white/40">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.payment_method}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
              {o.items.map((i, idx) => (
                <p key={idx} className="text-sm text-white/80">{i.product_name} · {i.size} × {i.quantity}</p>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Link to={`/review?order=${o.order_number}&email=${encodeURIComponent(account.email)}`} className="font-mono-cap text-[10px] text-[#FF69B4] underline underline-offset-4">Review this order →</Link>
              <p className="text-sm font-medium">₹{o.total.toLocaleString("en-IN")}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-3xl mt-14">Your reviews.</h2>
      {account.reviews.length === 0 ? (
        <p className="text-sm text-white/40 mt-3">No reviews yet. <Link to="/review" className="text-[#FF69B4] underline">Leave one →</Link></p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {account.reviews.map((r) => {
            const p = getProductBySlug(r.product_id);
            return (
              <div key={r.id} data-testid="account-review-card" className="border border-white/10 bg-[#0a0a0a] p-5">
                <div className="flex items-center justify-between">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} className={r.rating >= n ? "text-[#FF69B4] fill-[#FF69B4]" : "text-white/20"} />
                    ))}
                  </div>
                  <p className="text-[10px] text-white/30">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                </div>
                <Link to={`/product/${r.product_id}`} className="text-xs text-[#FF69B4] mt-3 block">{p ? p.name : r.product_id}</Link>
                <p className="text-sm text-white/80 mt-2 leading-relaxed">{r.text}</p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
